import React from 'react';
import {
  ApolloClient,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
} from '@apollo/client';
import { withApollo } from 'next-with-apollo';
import { NextPageContext } from 'next';
import fetch from 'isomorphic-unfetch';

const GRAPHQL_TIMEOUT_MS = 10000;

type FetchOptions = RequestInit & { timeout?: number };

const fetchWithTimeout = (
  uri: RequestInfo,
  options?: FetchOptions,
): Promise<Response> =>
  new Promise((resolve, reject) => {
    const requestOptions: FetchOptions = {
      ...options,
      timeout: GRAPHQL_TIMEOUT_MS,
    };
    const timeout = setTimeout(
      () =>
        reject(
          new Error(`GraphQL request timed out after ${GRAPHQL_TIMEOUT_MS}ms`),
        ),
      GRAPHQL_TIMEOUT_MS,
    );

    fetch(uri, requestOptions as RequestInit)
      .then(resolve, reject)
      .finally(() => clearTimeout(timeout));
  });

export const apolloClient = ({
  initialState,
  ctx,
}: {
  initialState?: NormalizedCacheObject;
  ctx?: NextPageContext;
} = {}): ApolloClient<unknown> =>
  new ApolloClient({
    link: new HttpLink({
      uri: URLS.graphql,
      credentials: 'include',
      fetch: fetchWithTimeout,
      headers: {
        Cookie: (!process.browser && ctx?.req?.headers?.cookie) || '',
      },
    }),
    cache: new InMemoryCache({ addTypename: false }).restore(
      initialState || {},
    ),
    ssrMode: !process.browser,
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'network-only',
        errorPolicy: 'ignore',
      },
      query: {
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
      },
    },
  });
export default withApollo(
  ({ initialState, ctx }) => {
    return apolloClient({ initialState, ctx });
  },
  {
    render: ({ Page, props }) => {
      return (
        <ApolloProvider client={props.apollo}>
          <Page {...props} />
        </ApolloProvider>
      );
    },
  },
);
